import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useToastStore } from "./toastStore";
import type { Toast, ToastVariant } from "./toastStore";

// ─── Per-variant design tokens ────────────────────────────────────────────────

const VARIANT: Record<
  ToastVariant,
  {
    accent: string; // left border + icon ring color
    iconBg: string; // icon badge background
    iconColor: string; // icon color
    bar: string; // progress bar fill
    title: string; // title text color
    Icon: () => JSX.Element;
  }
> = {
  error: {
    accent: "border-l-red-500",
    iconBg: "bg-red-500/15",
    iconColor: "text-red-400",
    bar: "bg-red-500",
    title: "text-white",
    Icon: () => (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  success: {
    accent: "border-l-[rgb(var(--cp))]",
    iconBg: "bg-[rgb(var(--cp)/0.15)]",
    iconColor: "text-[rgb(var(--cp))]",
    bar: "bg-[rgb(var(--cp))]",
    title: "text-white",
    Icon: () => (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m4.5 12.75 6 6 9-13.5"
        />
      </svg>
    ),
  },
  info: {
    accent: "border-l-[rgb(var(--cs))]",
    iconBg: "bg-[rgb(var(--cs)/0.15)]",
    iconColor: "text-[rgb(var(--cs))]",
    bar: "bg-[rgb(var(--cs))]",
    title: "text-white",
    Icon: () => (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-1 4a1 1 0 0 1 2 0v3a1 1 0 1 1-2 0v-3Z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
};

// ─── Single toast ─────────────────────────────────────────────────────────────

function ToastItem({ toast }: { toast: Toast }) {
  const remove = useToastStore((s) => s.remove);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(Date.now());
  const remainingRef = useRef(toast.duration);
  const v = VARIANT[toast.variant];

  // Slide in on mount (needs a frame so the initial off-screen state renders first)
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Countdown progress bar
  useEffect(() => {
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(
        0,
        ((remainingRef.current - elapsed) / toast.duration) * 100,
      );
      setProgress(pct);
      if (pct <= 0) dismiss();
    }, 50);
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function pauseTimer() {
    clearTimer();
    remainingRef.current -= Date.now() - startRef.current;
  }

  function resumeTimer() {
    if (intervalRef.current) return;
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(
        0,
        ((remainingRef.current - elapsed) / toast.duration) * 100,
      );
      setProgress(pct);
      if (pct <= 0) dismiss();
    }, 50);
  }

  function dismiss() {
    clearTimer();
    setVisible(false);
    setTimeout(() => remove(toast.id), 400);
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      style={{ transitionDuration: "380ms" }}
      className={`
        relative w-[22rem] rounded-xl overflow-hidden
        bg-zinc-900/95 backdrop-blur-md
        border border-zinc-700/60 border-l-4 ${v.accent}
        shadow-2xl shadow-black/60
        transition-all ease-out
        ${visible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
      `}
    >
      {/* Main content */}
      <div className="flex items-start gap-3 px-4 py-4">
        {/* Icon badge */}
        <div
          className={`shrink-0 w-8 h-8 rounded-lg ${v.iconBg} flex items-center justify-center mt-0.5`}
        >
          <span className={v.iconColor}>
            <v.Icon />
          </span>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className={`text-sm font-semibold leading-snug ${v.title}`}>
            {toast.title}
          </p>
          {toast.message && (
            <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
              {toast.message}
            </p>
          )}
        </div>

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="shrink-0 mt-0.5 p-1 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700/60 transition-colors"
          aria-label="Dismiss notification"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Progress track */}
      <div className="h-0.5 bg-zinc-800">
        <div
          className={`h-full ${v.bar} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ─── Container ────────────────────────────────────────────────────────────────

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 pointer-events-none"
    >
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
}
